/**
 * Life4Earth V2 — React hook for project role slots data.
 *
 * Loads role slots (with assignments) for a project.
 * Automatically refreshes when the project roles cache is invalidated
 * (e.g. after a slot is created or assignment is made via SignalR).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchProjectRoleSlots,
  fetchProjectRoleSlotSummary,
} from "./projectRoles";
import type {
  ProjectRoleSlotResponse,
  ProjectRoleSlotSummary,
} from "./projectRoles";
import { fetchForKey, CACHE_UPDATE_EVENT, QUERY_KEYS } from "./queryCache";

// ── Result shape ────────────────────────────────────────────

export interface UseApiProjectRolesResult {
  /** Active role slots (excludes closed). */
  slots: ProjectRoleSlotResponse[];
  /** Closed (archived) role slots. */
  closedSlots: ProjectRoleSlotResponse[];
  /** Aggregate summary (total, filled, open, by category). */
  summary: ProjectRoleSlotSummary | null;
  loading: boolean;
  error: string | null;
  source: "api" | "empty" | "loading";
  /** Force a refetch (e.g. after a local mutation). */
  refetch: () => void;
}

interface CachedProjectRolesData {
  slots: ProjectRoleSlotResponse[];
  summary: ProjectRoleSlotSummary;
}

// ── Hook ────────────────────────────────────────────────────

/**
 * Load role slots data for a single project.
 * Pass null/undefined projectGuid to skip loading.
 */
export function useApiProjectRoles(
  projectGuid: string | null | undefined
): UseApiProjectRolesResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiProjectRolesResult>({
    slots: [],
    closedSlots: [],
    summary: null,
    loading: true,
    error: null,
    source: "loading",
    refetch: () => {},
  });

  const cacheKey = projectGuid
    ? `projectRoles:${projectGuid}`
    : null;

  const refetch = useCallback(async () => {
    if (!mountedRef.current || !projectGuid) return;

    try {
      const cached = await fetchForKey(
        cacheKey!,
        async (): Promise<CachedProjectRolesData> => {
          const [slots, summary] = await Promise.all([
            fetchProjectRoleSlots(projectGuid),
            fetchProjectRoleSlotSummary(projectGuid),
          ]);
          return { slots, summary };
        },
        { force: true }  // Always bypass cache on explicit refetch
      );

      if (!mountedRef.current) return;

      // Partition: active slots vs. closed (archived) slots
      const activeSlots = cached.slots.filter((s) => s.status !== "closed");
      const closedSlots = cached.slots.filter((s) => s.status === "closed");

      setData({
        slots: activeSlots,
        closedSlots,
        summary: cached.summary,
        loading: false,
        error: null,
        source: "api",
        refetch,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setData((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : "Failed to load role slots",
        source: "api",
        refetch,
      }));
    }
  }, [projectGuid, cacheKey]);

  // Initial load
  useEffect(() => {
    mountedRef.current = true;
    if (projectGuid) {
      refetch();
    } else {
      setData({
        slots: [],
        closedSlots: [],
        summary: null,
        loading: false,
        error: null,
        source: "empty",
        refetch,
      });
    }
    return () => {
      mountedRef.current = false;
    };
  }, [projectGuid, refetch]);

  // Listen for cache invalidation events (SignalR-driven)
  useEffect(() => {
    if (!cacheKey) return;

    const handleCacheUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === cacheKey) {
        refetch();
      }
    };

    window.addEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
  }, [cacheKey, refetch]);

  return data;
}
